// src/components/LineStatusBadge.jsx
//
// Badge de estado de la línea O/U con contador de edad en tiempo real.
// Muestra siempre los segundos transcurridos desde el último update del
// backend para que el cliente pueda decidir si la línea es accionable.
//
// 3 estados visuales:
//   0-20s  → "En vivo · Ns"           (verde + dot pulsante)
//   20-40s → "Sincronizando · Ns"     (naranja, tooltip explicativo)
//   40s+   → "Línea no disponible · Ns" (gris)
//
// Backend (bot/nba_ou_engine.py) marca lineSource:
//   "betsapi"       → fresh, mostrar verde/naranja según edad
//   "stale_expired" → cache > 40s, mostrar gris (con edad si hay)
//   "manual" / null → sin cobertura, mostrar gris (sin edad)
//
// El componente actualiza el contador cada 1s con setInterval.
// useEffect depende de lineTs: cuando llega un timestamp nuevo del
// backend, el effect se recrea y resetea age a 0 automáticamente.

import { useEffect, useState } from 'react';
import Tooltip from './Tooltip';

const FRESH_THRESHOLD_S    = 20;   // <= 20s → verde
const SYNCING_THRESHOLD_S  = 40;   // <= 40s → naranja; > 40s → gris

// Copy educativo cuando la liga no tiene cobertura de odds en el proveedor
// (BetsAPI). Distinto de "stale_expired" (la línea existía y se enfrió):
// acá NUNCA hubo timestamp → es cobertura, no incidente. Tooltip solo en
// este caso para no confundir con freshness.
const NO_COVERAGE_TOOLTIP =
  'Esta liga no tiene cuota disponible en nuestro proveedor. El motor proyecta el puntaje pero no puede calcular el edge.';

export default function LineStatusBadge({ lineSource, lineTs }) {
  const [age, setAge] = useState(null);

  useEffect(() => {
    if (!lineTs) {
      setAge(null);
      return undefined;
    }
    const tick = () => {
      setAge(Math.max(0, Math.floor((Date.now() - lineTs) / 1000)));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [lineTs]);

  // Estado: línea explícitamente descartada por backend (cache > 40s) o
  // sin cobertura ("manual"/null). Mostrar gris con edad si está disponible.
  const isUnavailableSource =
    lineSource === 'stale_expired' ||
    lineSource === 'manual' ||
    !lineSource;

  if (isUnavailableSource) {
    // age === null → nunca hubo timestamp → es cobertura del proveedor
    // (BetsAPI no cubre esta liga). Mostramos tooltip ⓘ educativo.
    // age !== null → la línea existía y se enfrió (stale_expired): el
    // "· Ns" inline ya comunica el estado, no necesita tooltip extra.
    const isCoverageGap = age === null;
    return (
      <span className="line-badge line-badge--unavailable">
        Línea no disponible{age !== null ? ` · ${age}s` : ''}
        {isCoverageGap && (
          <span style={{ marginLeft: 5, display: 'inline-flex', verticalAlign: 'middle' }}>
            <Tooltip text={NO_COVERAGE_TOOLTIP} maxWidth={240} />
          </span>
        )}
      </span>
    );
  }

  // Defensa: si por algún motivo el backend nos mandó "betsapi" pero la
  // edad es > 40s (clock drift, race condition), mostrar como no disponible.
  if (age !== null && age > SYNCING_THRESHOLD_S) {
    return (
      <span className="line-badge line-badge--unavailable">
        Línea no disponible · {age}s
      </span>
    );
  }

  // Sincronizando: 20s < edad <= 40s
  if (age !== null && age > FRESH_THRESHOLD_S) {
    return (
      <span
        className="line-badge line-badge--syncing"
        title={`La línea se está actualizando. Última info hace ${age}s.`}
      >
        Sincronizando · {age}s
      </span>
    );
  }

  // En vivo: 0-20s (también cubre el caso age === null si lineTs vino
  // pero todavía no corrió el primer tick, pero como llamamos tick() de
  // entrada, age siempre tiene valor cuando hay lineTs).
  const liveAge = age ?? 0;
  return (
    <span className="line-badge line-badge--live">
      <span className="line-badge__dot" />
      En vivo · {liveAge}s
    </span>
  );
}
